import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Typography,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Autocomplete,
  CircularProgress,
  Divider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import {
  format,
  isBefore,
  isEqual,
  isAfter,
  parseISO,
  parse,
  startOfDay,
  isValid,
} from "date-fns";

import { useTranslation } from "react-i18next";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  booleanOptions,
  officeOptions,
  ufOptions,
} from "../utils/optionsSelectForms";

import { useCreateWorker } from "services/input-core/workers/set-workers-acelen";
import { toast } from "react-toastify";
import { useSowWorkerRoles } from "services/input-core/sows/get-sow-worker-roles";
import { checkMessageErrors } from "utils/functions";
import { useSubscription } from "store/auth/auth-store";
import { useSOWsQuery } from "services/input-core/sows/get-all-sows";
import { useWorkersQuery } from "services/input-core/workers/get-all-workers";
import { FC, ReactNode } from "react";
import InputMask, { Props } from "react-input-mask";
// Campos comentados segundo pedido da acelen para a determinada sprint-60
import { useGetPickList } from "services/input-core/pick-lists/get-ticket-list";

type NewEquipmentNames =
  | "nationality"
  | "model"
  | "brand"
  | "renavam"
  | "carLicensePlate"
  | "firstName"
  | "lastName"
  | "birthDate"
  | "motherName"
  | "email"
  | "cep"
  | "address"
  | "addressNumber"
  | "city"
  | "uf"
  | "state"
  | "workerType"
  | "routineStopWorker"
  | "temporaryWorker"
  | "startDate"
  | "endDate"
  | "unitOfMeasure"
  | "hoursPerDay"
  | "hoursPerWeek"
  | "rateCategoryPr"
  | "rateCategoryBr"
  | "siteCode"
  | "location"
  | "sowWorkerRole"
  | "sowId"
  | "driver"
  | "workerCountryOrigin"
  | "generalRegister"
  | "comment"
  // Campos comentados segundo pedido da acelen para a determinada sprint-60
  | "subcontractor"
  | "subcontractorCompany"
  | "department";

type TInputMaskCorrect = Omit<Props, "children"> & {
  children?: () => JSX.Element;
  maskChar?: string | null;
};

const InputMaskCorrect: FC<TInputMaskCorrect> = ({ children, ...props }) => {
  const child = (children as unknown) as ReactNode;
  return <InputMask children={child} {...props} />;
};

export const FormCreateEquipment = () => {
  const { t } = useTranslation([
    "form-create-worker",
    "list-messages",
    "list-errors",
  ]);

  const newWorkerSchema = z
    .object({
      // Documentos de trabalhador
      nationality: z
        .string({
          required_error: t("required-selection-error"),
        })
        .default("Brasileira (o)"),
      model: z.string({
        required_error: t("field-required"),
      }),
      brand: z.string({
        required_error: t("field-required"),
      }),
      renavam: z
        .string({
          required_error: t("field-required"),
        })
        .regex(/^[0-9]{11}$/, t("enter-valid-renavam")),
      carLicensePlate: z
        .string({
          required_error: t("field-required"),
        })
        .regex(
          /^[a-zA-Z]{3}-?[0-9]{4}$|^[a-zA-Z]{3}-?[0-9]{1}[a-zA-Z]{1}[0-9]{2}$/,
          t("enter-valid-plate"),
        ),
      generalRegister: z.string().default("NA"),

      // Dados do trabalhador
      firstName: z.string().default("NA"),
      lastName: z.string().default("NA"),
      email: z
        .string({
          required_error: t("field-required"),
        })
        .regex(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          t("invalid-email-error"),
        ),
      birthDate: z.string({
        required_error: t("field-required"),
      }),
      motherName: z.string().default("NA"),

      // Dados de endereço
      cep: z.string().default("NA"),
      address: z.string().default("NA"),
      addressNumber: z.string().default("NA"),
      city: z.string().default("NA"),
      state: z.string().default("NA"),
      uf: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(1, t("state-error")),

      // Dados de trabalho
      unitOfMeasure: z.string().default("NA"),
      hoursPerDay: z.string().default("NA"),
      hoursPerWeek: z.string().default("NA"),
      rateCategoryBr: z.string().default("NA"),
      rateCategoryPr: z.string().default("NA"),

      // Tipo de trabalhador
      startDate: z.string({
        required_error: t("field-required"),
      }),
      endDate: z.string({
        required_error: t("field-required"),
      }),

      workerType: z.string().default("vehicle"),
      temporaryWorker: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(2, t("worker-type-error")),
      routineStopWorker: z.string().default("NA"),

      // Dados complementares de sow/worker
      workerCountryOrigin: z.string().default("Brasil"),
      sowId: z
        .string({
          required_error: t("required-selection-error"),
        })
        .regex(/[A-Z\d]/g, t("valid-code-error")),
      driver: z
        .array(z.string().regex(/[A-Z\d]/, { message: t("valid-code-error") }))
        .min(1, t("required-selection-error")),
      sowWorkerRole: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(1, t("code-selection-error")),
      siteCode: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(1, t("code-selection-error")),
      location: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(1, t("code-selection-error")),
      comment: z.string().optional(),
      sowStartDate: z.date().optional(),
      sowEndDate: z.date().optional(),

      // Campos comentados segundo pedido da acelen para a determinada sprint-60
      subcontractorCompany: z.string().optional(),
      subcontractor: z
        .string({ required_error: t("required-selection-error") })
        .min(2, t("worker-type-error")),
      department: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(3, t("code-selection-error")),
    })
    .superRefine((fields, ctx) => {
      if (
        isAfter(
          startOfDay(parseISO(fields.startDate)),
          startOfDay(parseISO(fields.endDate)),
        ) ||
        isEqual(
          startOfDay(parseISO(fields.startDate)),
          startOfDay(parseISO(fields.endDate)),
        )
      ) {
        ctx.addIssue({
          path: ["endDate"],
          code: z.ZodIssueCode.invalid_date,
          message: t("start-date-greater-than-end-date"),
        });
      }

      if (
        isBefore(
          startOfDay(parseISO(fields.startDate)),
          startOfDay(fields.sowStartDate),
        )
      ) {
        ctx.addIssue({
          path: ["startDate"],
          code: z.ZodIssueCode.invalid_date,
          message: t("start-dates-not-between-dates-from-sow"),
        });
      }
      // Campos comentados segundo pedido da acelen para a determinada sprint-60
      if (fields.subcontractor === "Yes" && !fields.subcontractorCompany) {
        ctx.addIssue({
          path: ["subcontractorCompany"],
          code: z.ZodIssueCode.invalid_string,
          validation: "regex",
          message: t("field-required"),
        });
      }
    })
    .transform((fields) => ({
      nationality: fields.nationality,
      model: fields.model,
      brand: fields.brand,
      renavam: fields.renavam,
      carLicensePlate: fields.carLicensePlate,
      firstName: fields.model,
      lastName: fields.brand,
      birthDate: `${fields["birthDate"]}-01-01`,
      motherName: fields.motherName,
      email: fields.email,
      cep: fields.cep ?? "",
      address: fields.address,
      addressNumber: fields.addressNumber,
      city: fields.city,
      uf: fields.uf,
      state: fields.uf,
      workerType: fields.workerType || "vehicle",
      routineStopWorker: fields.routineStopWorker,
      temporaryWorker: fields.temporaryWorker,
      startDate: fields.startDate,
      endDate: fields.endDate,
      unitOfMeasure: fields.unitOfMeasure,
      hoursPerDay: fields.hoursPerDay,
      hoursPerWeek: fields.hoursPerWeek,
      rateCategoryPr: fields.rateCategoryPr,
      rateCategoryBr: fields.rateCategoryBr,
      siteCode: fields.siteCode,
      location: fields.location,
      sowWorkerRole: fields.sowWorkerRole,
      sowId: fields.sowId,
      driver: fields.driver,
      workerCountryOrigin: fields.workerCountryOrigin,
      generalRegister: fields.generalRegister,
      comment: fields.comment,
      sowStartDate: fields.sowStartDate,
      sowEndDate: fields.sowEndDate,
      // Campos comentados segundo pedido da acelen para a determinada sprint-60
      department: fields.department,
      subcontractorCompany: fields.subcontractorCompany,
      subcontractor: fields.subcontractor,
    }));

  type NewWorkerFormFields = z.infer<typeof newWorkerSchema>;

  const {
    handleSubmit,
    register,
    watch,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<NewWorkerFormFields>({
    mode: "all",
    criteriaMode: "all",
    resolver: zodResolver(newWorkerSchema),
  });

  const {
    data: sowWorkerRoles,
    isLoading: isSowWorkerRolesLoading,
  } = useSowWorkerRoles("equipment");

  const {
    mutate: setCreateWorker,
    isLoading: isCreateWorker,
  } = useCreateWorker();

  const { subscription } = useSubscription();

  const { data: Sows, isLoading: isSowsLoading } = useSOWsQuery(
    subscription?.subscriptionId,
    {
      enabled: !!subscription?.subscriptionId,
    },
  );

  const { data: Workers, isLoading: isWorkersLoading } = useWorkersQuery(
    subscription?.subscriptionId,
    {
      enabled: !!subscription?.subscriptionId,
    },
  );

  // Campos comentados segundo pedido da acelen para a determinada sprint-60
  const {
    data: dataPickListDepartamento,
    isLoading: isLoadingPickListDepartamento,
  } = useGetPickList("Departamento");

  const renderTextField = (
    name: NewEquipmentNames,
    label: string,
    required = true,
    disabled = false,
    type = "text",
    multiline = false,
    rows = 1,
    maxRows = 1,
    mask?: string,
  ) => (
    <Box display="flex" flexDirection="column" width="100%">
      {mask ? (
        <Controller
          data-cy={`controller-${name}`}
          name={name}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <InputMaskCorrect
              {...field}
              mask={mask}
              maskChar={null}
              onChange={(e) => {
                field.onChange(e);
                setValue(name, e.target.value);
              }}
              value={field.value}
              disabled={disabled}
            >
              {() => (
                <TextField
                  data-cy={`input-${name}`}
                  id={name}
                  variant="outlined"
                  label={t(label)}
                  required={required}
                  type={type}
                  style={{ width: "100%" }}
                  multiline={multiline}
                  rows={rows}
                  defaultValue=""
                  maxRows={maxRows}
                />
              )}
            </InputMaskCorrect>
          )}
        />
      ) : (
        <Controller
          data-cy={`controller-${name}`}
          name={name}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              data-cy={`input-${name}`}
              id={name}
              variant="outlined"
              label={t(label)}
              required={required}
              type={type}
              style={{ width: "100%" }}
              multiline={multiline}
              rows={rows}
              defaultValue=""
              maxRows={maxRows}
              disabled={disabled}
              InputProps={{
                sx: {
                  background: disabled ? "rgba(0, 0, 0, 0.036)" : "initial",
                },
              }}
              {...register(name, { required: true })}
            />
          )}
        />
      )}
      {errors[name] && (
        <Typography color="error" variant="caption" mt={1}>
          {errors[name]?.message}
        </Typography>
      )}
    </Box>
  );

  const renderDatePicker = (
    name: NewEquipmentNames,
    label: string,
    required: boolean,
    minDate?: Date,
    maxDate?: Date,
    disabled?: boolean,
  ) => (
    <Box display="flex" flexDirection="column" width="100%">
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field, fieldState: { error } }) => (
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <DatePicker
              {...field}
              data-cy={`date-picker-${name}`}
              label={t(label)}
              disabled={disabled}
              defaultValue={null}
              minDate={minDate}
              maxDate={maxDate}
              shouldDisableDate={(day) => minDate && isBefore(day, minDate)}
              onChange={(date) => {
                let dateString = "";
                if (date) {
                  if (isValid(date)) {
                    dateString = format(date, "yyyy-MM-dd");
                  }
                }
                field.onChange(dateString);
                setValue(name, dateString);
              }}
              value={
                Array.isArray(field.value) && field.value.length > 0
                  ? parseISO(field.value[0]) // Parse ISO string
                  : field.value
                  ? parseISO(field.value as string)
                  : null
              }
              slotProps={{
                textField: {
                  required: required,
                  error: Boolean(errors[name]),
                  helperText: errors[name]?.message,
                },
              }}
            />
          </LocalizationProvider>
        )}
      />
    </Box>
  );

  const renderAutocompleteField = (
    name: NewEquipmentNames,
    label: string,
    options: { value: string | string; label: string }[],
    isMultiple = false,
    disabled = false,
    isLoading = false,
  ) => (
    <Box display="flex" flexDirection="column" width="100%">
      <Controller
        data-cy={`controller-${name}`}
        name={name}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <Autocomplete
            {...field}
            data-cy={`autocomplete-${name}`}
            id={name}
            key={field?.value?.toString() || name}
            limitTags={4}
            multiple={isMultiple}
            options={options}
            getOptionLabel={(option: { value: string; label: string }) =>
              option?.label
            }
            disabled={isLoading || disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                data-cy={`input-${name}`}
                label={t(label)}
                error={Boolean(errors[name])}
                helperText={errors[name]?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress
                          size={20}
                          sx={{ marginRight: "1rem" }}
                        />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={(_, newValue: any) => {
              if (!newValue) {
                setValue(name, undefined);
              } else if (isMultiple) {
                const selectedValues = newValue.map(
                  (option: { value: string; label: string }) => option.value,
                );
                field.onChange(selectedValues);
                setValue(name, selectedValues);
              } else {
                field.onChange(newValue.value);
                setValue(name, newValue.value);
                if (name === "sowId") {
                  setValue(
                    "sowStartDate",
                    parse(newValue.startDate, "dd/MM/yyyy", new Date()), // Use parse do date-fns
                  );
                  setValue(
                    "sowEndDate",
                    parse(newValue.endDate, "dd/MM/yyyy", new Date()), // Use parse do date-fns
                  );
                }
              }
            }}
            value={
              isMultiple
                ? (field?.value as string[])?.map(
                    (value) =>
                      options.find((option) => option.value === value) ||
                      undefined,
                  )
                : options?.find((option) => option.value === field.value) ||
                  undefined
            }
          />
        )}
      />
    </Box>
  );

  const renderToggleButtonGroup = (
    name: NewEquipmentNames,
    label: string,
    options: any[],
  ) => (
    <Box display="flex" flexDirection="column" mb={2}>
      <Typography variant="subtitle2" mb={1}>
        {t(label)}
      </Typography>
      <ToggleButtonGroup
        data-cy={`buttons-group-${name}`}
        color="primary"
        value={watch(name) || ""}
        exclusive
        name={name}
        {...register(name, { required: true })}
        onChange={(e: any) => {
          setValue(name, e.target.value, {
            shouldValidate: true,
          });
        }}
      >
        {options?.map((option: any) => (
          <ToggleButton
            data-cy={`button-${name}`}
            key={`${name}-${option.value}`}
            value={option.value}
            sx={{ width: "100%", padding: "15.5px 14px" }}
          >
            {t(option.label)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {errors[name] && (
        <Typography color="error" variant="caption" mt={1}>
          {errors[name]?.message}
        </Typography>
      )}
    </Box>
  );

  const onSubmit = async (data: NewWorkerFormFields) => {
    toast.info(
      t("info-create-equipment", {
        ns: "list-messages",
      }),
    );

    data.workerType = "vehicle";

    delete data.sowStartDate;
    delete data.sowEndDate;

    setCreateWorker(
      {
        ...data,
      },
      {
        onSuccess: () => {
          toast.success(
            t("success-create-equipment", {
              ns: "list-messages",
            }),
          );
          reset();
          setValue("temporaryWorker", "");
          setValue("subcontractor", "");
          setValue("comment", "");
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_CREATE_WORKER",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sm={12}
      lg={12}
      md={12}
    >
      <Box sx={{ width: "90%", maxWidth: "60vw", p: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)} data-cy="create-equipment-form">
          {isSowsLoading || isWorkersLoading || isSowWorkerRolesLoading ? (
            <CircularProgress />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4" color="rgba(0,0,0,0.75)">
                  {t("header-form-vh")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                {renderTextField(
                  "email",
                  "contact-email",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderTextField(
                  "carLicensePlate",
                  "car-plate",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                  "aaa9*99",
                )}
              </Grid>

              <Grid item xs={6}>
                {renderTextField(
                  "renavam",
                  "renavam",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                  "99999999999",
                )}
              </Grid>

              <Grid item xs={6}>
                {renderTextField("brand", "car-make", true, false, "text")}
              </Grid>

              <Grid item xs={6}>
                {renderTextField("model", "car-model", true, false, "text")}
              </Grid>

              <Grid item xs={6}>
                {renderTextField(
                  "birthDate",
                  "manufacture-year",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                  "9999",
                )}
              </Grid>

              <Grid item xs={6}>
                {renderAutocompleteField(
                  "uf",
                  "select-state-uf-plate",
                  ufOptions,
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="h4"
                  color="rgba(0,0,0,0.75)"
                  marginTop="1rem"
                >
                  {t("header-form-worker")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={6}>
                {renderToggleButtonGroup(
                  "temporaryWorker",
                  "temporary-vehicle",
                  booleanOptions,
                )}
              </Grid>
              <Grid item xs={6}></Grid>
              {/* Campos comentados segundo pedido da acelen para a determinada sprint-60 */}
              <Grid item xs={6} alignContent="end">
                {renderToggleButtonGroup(
                  "subcontractor",
                  "subcontractor-vh",
                  booleanOptions,
                )}
              </Grid>

              <Grid item xs={6} alignContent="end" marginBottom="1rem">
                {renderTextField(
                  "subcontractorCompany",
                  "subcontractorCompany",
                  false,
                  watch("subcontractor") === "Yes" ? false : true,
                  "text",
                )}{" "}
              </Grid>
              {/* sprint-60 */}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={6}>
                {renderAutocompleteField(
                  "sowId",
                  "statement-of-work-id",
                  Sows?.map((option) => ({
                    value: option.sowID,
                    label: option.sowID,
                    startDate: option.startDate,
                    endDate: option.endDate,
                  })),
                )}
              </Grid>

              {/* Campos comentados segundo pedido da acelen para a determinada sprint-60 */}
              <Grid item xs={6}></Grid>
              <Grid item xs={6}>
                {renderAutocompleteField(
                  "department",
                  "department",
                  dataPickListDepartamento?.values?.map((value) => ({
                    value: value,
                    label: value,
                  })),
                  false,
                  !Array.isArray(dataPickListDepartamento?.values),
                  isLoadingPickListDepartamento,
                )}
              </Grid>

              {/* sprint-60 */}

              <Grid item xs={6}>
                {renderAutocompleteField(
                  "sowWorkerRole",
                  "sow-worker-role-vh",
                  sowWorkerRoles?.map((option) => ({
                    value: option.name,
                    label: option.name,
                  })),
                )}
              </Grid>

              <Grid item xs={6}>
                {renderAutocompleteField(
                  "siteCode",
                  "site-code-site-code-star",
                  officeOptions,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderAutocompleteField(
                  "location",
                  "location-code",
                  officeOptions,
                )}
              </Grid>

              <Grid item xs={12}>
                {renderAutocompleteField(
                  "driver",
                  "driver",
                  Workers?.map((option) => ({
                    value: option.workerID,
                    label: option.workerID,
                  })),
                  true,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderDatePicker(
                  "startDate",
                  "start-date",
                  true,
                  watch("sowStartDate"),
                  startOfDay(watch("sowEndDate")),
                  !watch("sowId"),
                )}
              </Grid>
              <Grid item xs={6}>
                {renderDatePicker(
                  "endDate",
                  "end-date",
                  true,
                  startOfDay(
                    parse(watch("startDate"), "yyyy-MM-dd", new Date()),
                  ),
                  startOfDay(watch("sowEndDate")),
                  !watch("startDate"),
                )}
              </Grid>

              <Grid item xs={12}>
                {renderTextField(
                  "comment",
                  "comment",
                  false,
                  false,
                  "text",
                  true,
                  4,
                  4,
                )}
              </Grid>

              <Grid item xs={12}>
                <Grid container justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isCreateWorker}
                  >
                    {isCreateWorker ? <CircularProgress /> : t("register")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        </form>
      </Box>
    </Grid>
  );
};
